import React, { useMemo, ReactNode, SyntheticEvent } from 'react';
import { PropertyViolation, Violation } from 'types';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Checkbox,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import { AUTURA_ASPHALT_LIGHT, AUTURA_GRAYSCALE } from 'context/themeContext';
import { TextValidator } from 'react-material-ui-form-validator';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useTranslation } from 'react-i18next';
import { ArrowDropDown } from '@mui/icons-material';

type ViolationsSelectProps = {
  disableAllViolations?: boolean;
  options: Violation[] | PropertyViolation[];
  selected: Violation[] | PropertyViolation[];
  handleChange: (value: Violation[] | PropertyViolation[]) => void;
  loading?: boolean;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  margin?: 'dense' | 'normal' | 'none';
  variant?: 'standard' | 'outlined' | 'filled';
  readonly?: boolean;
};

const ViolationsSelect = ({
  disableAllViolations = false,
  options,
  selected,
  handleChange,
  loading = false,
  required = false,
  disabled = false,
  helperText,
  size,
  margin = 'none',
  variant = 'outlined',
  readonly = false,
}: ViolationsSelectProps): ReactNode => {
  const { t } = useTranslation();

  const sortedOptions = useMemo(
    () =>
      options
        .filter((option: Violation | PropertyViolation) => {
          if (disableAllViolations && option.name === 'All Violations') {
            return false;
          }
          return true;
        })
        .sort(
          (
            a: Violation | PropertyViolation,
            b: Violation | PropertyViolation,
          ) => {
            if (a.name === 'All Violations') {
              return -1;
            } else if (b.name === 'All Violations') {
              return 1;
            }
            return a.name.localeCompare(b.name);
          },
        ),
    [options, disableAllViolations],
  );

  const getOptionLabel = (option: Violation | PropertyViolation) => {
    if ('tagRequired' in option) {
      if (option.tagHours > 1) {
        return `${option.name} (${option.tagHours}hr ${'tag required'})`;
      } else {
        return `${option.name}`;
      }
    }
    return `${option.name}`;
  };

  return (
    <Autocomplete
      multiple
      fullWidth
      size={size || 'medium'}
      options={sortedOptions}
      getOptionLabel={getOptionLabel}
      value={selected}
      onChange={(e: SyntheticEvent, value) => {
        value && handleChange(value);
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      disableCloseOnSelect
      disabled={loading || disabled}
      popupIcon={
        loading ? (
          <CircularProgress size={20} sx={{ color: AUTURA_ASPHALT_LIGHT }} />
        ) : (
          <ArrowDropDown />
        )
      }
      renderOption={(props, option, { selected }) => {
        return (
          <MenuItem
            {...props}
            key={option.id}
            sx={{ padding: 0, backgroundColor: AUTURA_GRAYSCALE[100] }}
            divider
          >
            <Checkbox
              icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
              checkedIcon={<CheckBoxIcon fontSize='small' />}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {getOptionLabel(option)}
          </MenuItem>
        );
      }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextValidator
          {...params}
          value={selected}
          variant={variant}
          margin={margin}
          label={`${t('violation.label_plural', { ns: 'entities' })}${
            required ? ' *' : ''
          }`}
          name='violations'
          validators={required ? ['required'] : []}
          helperText={helperText}
          errorMessages={[
            t('errors.required', {
              name: t('violation.label_plural', { ns: 'entities' }),
            }),
          ]}
          InputProps={{
            ...params.InputProps,
            readOnly: readonly,
          }}
          inputProps={{
            ...params.inputProps,
            style: {
              ...params.inputProps.style,
              cursor: readonly ? 'default' : 'text',
            },
          }}
        />
      )}
    />
  );
};

export default ViolationsSelect;
